import {useCallback, useMemo, useState} from 'react';

import GlobusIcon from '@/assets/icons/globus.svg?react';
import BaseModal from '@/shared/components/modals/base/BaseModal';
import ModalHeader from '@/shared/components/modals/base/ModalHeader';
import {useNavigateToOtherLangOfPage} from '@/shared/hooks/useNavigateToOtherLangOfPage';
import {useToggle} from '@/shared/hooks/useToggle';
import {
  APP_LANGUAGE_SUPPORTS,
  AppLanguage,
  getCountyShortNameByCode,
} from '@/shared/lib/appLanguageSupports';
import {getLanguage, localize, setLanguage} from '@/shared/services/i18n';

import PageLanguageSwitcherItem from './PageLanguageSwitcherItem';

function PageLanguageSwitcher() {
  const language = getLanguage();
  const shortLanguage = useMemo(() => {
    return getCountyShortNameByCode(language);
  }, [language]);
  const languageModal = useToggle(false);
  const navigateToOtherLangOfPage = useNavigateToOtherLangOfPage();
  const [applyingLanguage, setApplyingLanguage] = useState<AppLanguage>();
  const handleSwitchLanguage = useCallback(
    async (lang: AppLanguage) => {
      setApplyingLanguage(lang);

      try {
        await setLanguage(lang);

        languageModal.hide();

        navigateToOtherLangOfPage(lang);
      } finally {
        setApplyingLanguage(undefined);
      }
    },
    [languageModal.hide, navigateToOtherLangOfPage]
  );

  const langList = useMemo(
    () =>
      Object.entries(APP_LANGUAGE_SUPPORTS).sort(([code]) =>
        code === language ? -1 : 0
      ),
    [language]
  );

  return (
    <>
      <button
        onClick={languageModal.show}
        className="rounded-3xl bg-white py-1 ps-2 pe-3 text-black outline-1 outline-stroke sm:p-2 sm:pe-4"
      >
        <span className="flex items-center gap-2 text-sm capitalize sm:gap-2 sm:text-xl">
          <GlobusIcon className="size-[1em]" />
          {shortLanguage}
        </span>
      </button>
      {languageModal.open && (
        <BaseModal onClose={languageModal.hide}>
          <div className="flex max-h-[inherit] min-h-0 min-w-[300px] max-w-[343px] flex-col overflow-clip rounded-2lg bg-white sm:max-w-[664px]">
            <ModalHeader
              className="shrink-0 p-4 font-semibold text-lg sm:p-5"
              onClose={languageModal.hide}
            >
              {localize('choose_language')}
            </ModalHeader>
            <div className="mt-1 min-h-0 flex-1 overflow-y-auto py-3">
              {langList.map(([code, name]) => (
                <PageLanguageSwitcherItem
                  key={code}
                  code={code as AppLanguage}
                  name={name.full}
                  selected={code === language}
                  applying={code === applyingLanguage}
                  onClick={handleSwitchLanguage}
                />
              ))}
            </div>
          </div>
        </BaseModal>
      )}
    </>
  );
}

export default PageLanguageSwitcher;
